import {
	computeContextMenuTitle,
	ContextMenu,
	GridView,
	LoadMoreIndicator,
	useContextMenu
} from "client/Components";
import {
	ReactElement,
	Ref,
	RefObject,
	forwardRef,
	useImperativeHandle,
	useMemo,
	useRef
} from "react";
import { DocumentGridViewItem } from "../DocumentGridViewItem";
import {
	MediaQueryKind,
	MultiSelectableOptions,
	SelectableItemOptions,
	useIsTouchDevice,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { BookmarkModel } from "client/Api/Documents/Bookmarks/bookmarks";
import { BookmarkAction, BookmarkActionContext } from "./bookmarkActions";
import { GridViewGroupItem, GridViewItemKind } from "client/Components/GridView/groupGridViewItems";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { getSharePointItemPreviewUrl } from "../SharePoint/SharePointGridView";
import { DocumentGridViewContainer } from "../DocumentGridViewContainer";
import { DocumentStoreType } from "client/Api/Documents/common";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { DocumentNoResult } from "../DocumentNoResult";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export type BookmarksGridViewProps = {
	isBusy: boolean;
	multiSelection: MultiSelectableOptions;
	selectionFor: (item: BookmarkModel) => SelectableItemOptions;
	items: readonly BookmarkModel[];
	selectionActions: readonly BookmarkAction[];
	actionContext: BookmarkActionContext;
	isInSelectMode: boolean;
	onOpenItem: (bookmark: BookmarkModel) => void;
	onEnableSelectMode: () => void;
	hasSearch: boolean;
};

export const BookmarksGridView = forwardRef(
	(props: BookmarksGridViewProps, ref: Ref<BookmarksGridViewHandle>): ReactElement => {
		const {
			isBusy,
			multiSelection,
			selectionFor,
			items,
			onOpenItem,
			selectionActions,
			actionContext,
			isInSelectMode,
			onEnableSelectMode,
			hasSearch
		} = props;

		const gridRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
		const [contextMenuHandler, contextMenuProps] = useContextMenu(gridRef);
		const isTouch: boolean = useIsTouchDevice();
		const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

		const { doesSupportThumbnails } = documentsConfiguration()
			.sharePoint;

		const itemsToRender = useMemo(
			(): readonly GridViewGroupItem<BookmarkModel>[] => {
				return items.map(
					x => ({
						model: x,
						uniqueKey: x.id,
						itemKind: x.itemType === DocumentItemType.Folder
							? GridViewItemKind.Default
							: GridViewItemKind.Preview
					})
				);
			},
			[items]
		);

		useImperativeHandle(
			ref,
			() => ({
				resetScrollPosition: ():void => {
					gridRef.current
						?.scrollTo(0 , 0);
				}
			}),
			[]
		);

		if (!isBusy && items.length === 0) {
			return (
				<DocumentNoResult
					hasSearch={hasSearch}
				/>
			);
		}

		return (
			<DocumentGridViewContainer>
				<GridView
					trackingId="bookmarks-grid-view"
					ref={gridRef}
					items={itemsToRender}
					hasLassoSelection={!isMobile}
					multiSelection={multiSelection}
					elementToRenderAfterItems={
						<>
							<LoadMoreIndicator
								hasMoreItems={false}
								isLoadingData={isBusy}
								onRequestMoreItems={() => { /** Noop */ }}
							/>
							{itemsToRender.length > 0 && (
								<AiAssistantFabSpacing />
							)}
						</>
					}
				>
					{(item) => {
						const {
							itemKind,
							model,
							uniqueKey
						} = item;

						const {
							id,
							itemType,
							name: givenName,
							extension
						} = model;

						const name: string = `${givenName}${extension}`;

						const previewUrl: string | undefined = model.storeType === DocumentStoreType.SharePoint
							&& doesSupportThumbnails
							&& model.itemType !== DocumentItemType.Folder
								? getSharePointItemPreviewUrl(model.id, model.location)
								: undefined;

						return (
							<DocumentGridViewItem
								key={uniqueKey}
								id={id}
								name={name}
								documentKind={itemType}
								selectable={selectionFor(model)}
								selectionActions={selectionActions}
								actionContext={{...actionContext, selection: [model]}}
								onContextMenu={contextMenuHandler}
								isInSelectMode={isInSelectMode}
								isMobile={isMobile}
								isTouch={isTouch}
								isPreview={itemKind === GridViewItemKind.Preview}
								previewUrl={previewUrl}
								onEnableSelectMode={onEnableSelectMode}
								onOpenItem={() => onOpenItem(model)}
								isBookmarked
							/>
						);
					}}
				</GridView>
				{contextMenuProps && (
					<ContextMenu
						{...contextMenuProps}
						actions={selectionActions}
						context={actionContext}
						trackingId="bookmark-context-menu"
						title={computeContextMenuTitle(
							actionContext.selection,
							(x) => x.name
						)}
					/>
				)}
			</DocumentGridViewContainer>
		);
	}
);

BookmarksGridView.displayName = "BookmarksGridView";

export type BookmarksGridViewHandle = {
	resetScrollPosition(): void;
};